import { normalizeMathDelimiters } from "@/util/mathDelimiters"
import { toText } from "@/util/toText"
import { cn } from "@heroui/theme"
import ReactMarkdown from "react-markdown"
import rehypeHighlight from "rehype-highlight"
import rehypeKatex from "rehype-katex"
import remarkBreaks from "remark-breaks"
import remarkGfm from "remark-gfm"
import remarkMath from "remark-math"
import { rehypeStripImages } from "./message/rehypeSafeHtml"
import "katex/dist/katex.min.css"

/**
 * 把用户生成的文本(问候语 / prompt 等)按 markdown 渲染为语义化 HTML，
 * 便于 SEO 抓取结构化内容(段落、列表、强调等)。
 * 出于安全考虑不启用 rehype-raw，原始 HTML 会被转义。
 * stripImages：机器人资料（问候语、记忆、示例对话等）不渲染图片，目前没有图片审核；Agent 消息里的图片照常显示。
 * 数学公式经 remark-math + KaTeX 渲染（\(...\)/\[...\] 预归一为 $ 定界符）；
 * 代码块语法高亮走 rehype-highlight（token 配色在 global.scss）。
 */
export const Markdown = ({ children, className, stripImages }: { children?: string | null, className?: string, stripImages?: boolean }) => {
  // 兜底：内容偶发为本地化对象时也能安全渲染
  const text = toText(children)
  if (!text) return null
  return (
    <ReactMarkdown
      remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
      rehypePlugins={stripImages ? [rehypeStripImages, rehypeKatex, rehypeHighlight] : [rehypeKatex, rehypeHighlight]}
      components={{
        // markdown 内链接统一新标签页打开（用户生成的问候语/prompt 里的外链居多），
        // 避免点击后离开当前会话/画布；rel 防止 window.opener 跨站泄露
        a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
      }}
      className={cn(
        "break-words leading-[1.6]",
        "[&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0",
        "[&_strong]:font-bold [&_em]:italic",
        "[&_a]:text-btn-primary [&_a]:underline [&_a]:break-all",
        "[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1",
        "[&_h1]:text-[18px] [&_h2]:text-[16px] [&_h3]:text-[15px] [&_h1]:font-bold [&_h2]:font-bold [&_h3]:font-bold [&_h1]:my-2 [&_h2]:my-2 [&_h3]:my-2",
        "[&_blockquote]:border-l-2 [&_blockquote]:border-theme-primary/20 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-theme-primary/60",
        "[&_code]:rounded [&_code]:bg-theme-primary/10 [&_code]:px-1 [&_code]:py-[1px]",
        // 代码块固定深底浅字（highlight.js token 配色按深底设计，明暗主题一致可读）
        "[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-[#0f1b2d] [&_pre]:p-3 [&_pre]:text-[#d6e2f0] [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-inherit",
        // KaTeX：块级公式可横向滚动，行内公式不吃 code 背景
        "[&_.katex-display]:my-2 [&_.katex-display]:overflow-x-auto [&_.katex-display]:overflow-y-hidden [&_.katex-display]:py-1",
        className,
      )}
    >{normalizeMathDelimiters(text)}</ReactMarkdown>
  )
}
